import React, { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { PageHeader } from '@shared/ui/pageHeader';
import { Tabs } from '@shared/ui/tabs';
import { ValueLabel } from '@features/ax/shared/ValueLabel';
import { Badge } from '@shared/ui/badge';
import { Button } from '@shared/ui/button';
import {
  Wrench,
  RefreshCw,
  Archive,
  Play,
  FileText,
  ClipboardList,
  Activity,
  Clock,
  MessageSquare,

} from 'lucide-react';
import { Details } from './tabs/Details';
import { Repair } from './tabs/Repair';
import { Replacement } from './tabs/Replacement';
import { Retirement } from './tabs/Retirement';
import { ServiceOrders } from './tabs/ServiceOrders';
import { ActivityLogTab } from '@features/mx/shared/components/ActivityLog';
import { useRemoteUtilityId } from '@shared/selectors/globalSelectors';
import { useOperationDetail, useStartOperation } from '@features/mx/operations/hooks';
import { useToast } from '@shared/hooks/use-toast';
import { QuickActions } from './quickActions';
import { NotesTab } from '@features/mx/shared/components/NotesTab';

export const Landing: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { operationId } = useParams();
  const [searchParams] = useSearchParams();
  const [isOperationStarted, setIsOperationStarted] = useState(false);
  const utilityId = useRemoteUtilityId();
  const { data: operationDetailData, refetch: refetchDetail } = useOperationDetail(operationId as string, utilityId, "details");
  const detail = operationDetailData?.result || {};
  const { mutate: startOperation, isPending: isStarting } = useStartOperation();
  const { toast } = useToast();

  const urlFrom = searchParams.get('from') || undefined;
  const [originFrom] = useState<string | undefined>(() => searchParams.get('from') || undefined);
  const effectiveOrigin = originFrom || urlFrom;

  const handleStartOperation = () => {
    if (!operationId) return;
    startOperation(operationId, {
      onSuccess: () => {
        setIsOperationStarted(true);
        refetchDetail();
        toast({ title: 'Success', description: 'Operation started successfully.' });
      },
      onError: (err: any) => {
        const apiMsg = err?.response?.data?.message || err?.message || 'Failed to start operation.';
        toast({ title: 'Error', description: apiMsg, variant: 'destructive' });
      }
    });
  };

  // Determine dynamic operation type (repair | replacement | retirement)
  let operationType = 'repair';
  const typeStr = detail.operationTypeDisplay?.toLowerCase() || '';
  if (typeStr.includes('retire')) operationType = 'retirement';
  if (typeStr.includes('replace')) operationType = 'replacement';

  const statusVal = Number(detail?.status ?? detail?.status_code ?? 0);
  const statusDisplayStr = String(detail?.statusDisplay || detail?.status_display || detail?.statusName || detail?.status_name || '').trim();

  const isRepairedInInventory =
    statusDisplayStr.toLowerCase() === 'repaired in inventory' ||
    statusDisplayStr.toLowerCase().includes('repaired in inventory') ||
    statusDisplayStr.toLowerCase().includes('repaired-in-inventory');

  const isRetired =
    statusDisplayStr.toLowerCase() === 'retired' ||
    statusDisplayStr.toLowerCase() === 'resource marked as retired' ||
    statusDisplayStr.toLowerCase() === 'marked as retired';

  const isCompleted =
    statusVal === 2 ||
    statusVal === 4 ||
    isRepairedInInventory ||
    isRetired;

  const isOperationInProgress = (isOperationStarted || statusDisplayStr === 'In Progress') && !isCompleted;

  // Dynamic middle tab definition based on operation type
  const actionTab = useMemo(() => {
    switch (operationType) {
      case 'replacement':
      case 'replace':
        return {
          key: 'replacement',
          label: 'Replacement',
          shortLabel: 'replacement',
          icon: <RefreshCw className="w-4 h-4 mr-2" />,
          component: <Replacement operation={detail} />,
          title: 'Replacement',
          badgeClass: 'bg-blue-50 text-blue-700 border-blue-200',
        };
      case 'retirement':
      case 'retire':
        return {
          key: 'retirement',
          label: 'Retirement',
          shortLabel: 'retirement',
          icon: <Archive className="w-4 h-4 mr-2" />,
          component: (
            <Retirement
              operation={detail}
              isOperationStarted={isOperationStarted}
              onStartOperation={() => setIsOperationStarted(true)}
              originFrom={effectiveOrigin}
            />
          ),
          title: 'Retirement',
          badgeClass: 'bg-rose-50 text-rose-700 border-rose-200',
        };
      case 'repair':
      default:
        return {
          key: 'repair',
          label: 'Repair',
          shortLabel: 'repair',
          icon: <Wrench className="w-4 h-4 mr-2" />,
          component: (
            <Repair
              operation={detail}
              isOperationStarted={isOperationStarted}
              onStartOperation={() => setIsOperationStarted(true)}
              originFrom={effectiveOrigin}
            />
          ),
          title: 'Repair',
          badgeClass: 'bg-amber-50 text-amber-700 border-amber-200',
        };
    }
  }, [operationType, isOperationStarted, detail, effectiveOrigin]);

  const tabComponents = {
    details: {
      label: 'Details',
      shortLabel: 'details',
      icon: <FileText className="w-4 h-4 mr-2" />,
      component: <Details operation={detail} />,
    },
    [actionTab.key]: {
      label: actionTab.label,
      shortLabel: actionTab.shortLabel,
      icon: actionTab.icon,
      component: actionTab.component,
    },
    'service-orders': {
      label: 'Service Orders',
      shortLabel: 'service-orders',
      icon: <ClipboardList className="w-4 h-4 mr-2" />,
      component: <ServiceOrders />,
    },
    notes: {
      label: "Notes",
      shortLabel: "notes",
      icon: <MessageSquare className="w-4 h-4 mr-2" />,
      component: <NotesTab requestId={operationId || ''} source='7' />,
    },
    'activity-log': {
      label: 'Activity Log',
      shortLabel: 'activity-log',
      icon: <Activity className="w-4 h-4 mr-2" />,
      component: <ActivityLogTab entityId={operationId || ''} entityType="operation" />,
    },
  };

  const urlMapping = {
    details: 'details',
    [actionTab.key]: actionTab.key,
    'service-orders': 'service-orders',
    "note": "note",
    'activity-log': 'activity-log',
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Top Page Header */}
      <PageHeader
        title="Operation Details"
        description="View and track operation progress, linked resource information, and execution steps."
        onBack={() => navigate('/mx/operations')}
      >
        {isCompleted ? (
          <Badge
            variant="outline"
            className="bg-emerald-50 text-emerald-700 border-emerald-200 font-medium text-xs px-3 py-1.5 rounded-full"
          >
            {detail.statusDisplay || detail.status_display || 'Completed'}
          </Badge>
        ) : isOperationInProgress ? (
          <Badge
            variant="outline"
            className="bg-emerald-50 text-emerald-700 border-emerald-200 font-medium text-xs px-3 py-1.5 rounded-full"
          >
            In Progress
          </Badge>
        ) : (
          <Button
            className=""
            onClick={handleStartOperation}
            disabled={isStarting}
          >
            <Play className="w-4 h-4 fill-current" />
            <span>{isStarting ? 'Starting...' : 'Mark as In-Progress'}</span>
          </Button>
        )}
      </PageHeader>

      {/* Operation Meta and Top Overview Card + Quick Actions Grid (8 & 4) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Top Overview Card (8 cols) */}
        <div className="lg:col-span-8 bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-5">
          {/* Badges and Title */}
          <div>
            <div className="flex items-center gap-2.5 flex-wrap mb-1.5">
              <span className="text-sm font-bold text-blue-600">
                {detail.operationNo || operationId || 'NA'}
              </span>
              <Badge
                variant="outline"
                className={`${actionTab.badgeClass} font-medium text-xs px-2.5 py-0.5 rounded-full`}
              >
                {actionTab.title}
              </Badge>
              <Badge
                variant="outline"
                className={`${isCompleted || isOperationInProgress
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-amber-50 text-amber-700 border-amber-200'
                  } font-medium text-xs px-2.5 py-0.5 rounded-full flex items-center gap-1`}
              >
                <Clock className="w-3 h-3" />
                <span>{detail.statusDisplay || detail.status_display || (isOperationInProgress ? 'In Progress' : 'NA')}</span>
              </Badge>

            </div>
            <h2 className="text-base font-bold text-gray-900">
              {detail.operationTypeDisplay || 'NA'}: {detail.resourceOverview?.resourceId || 'NA'}
            </h2>
          </div>

          {/* Operation Details Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-y-4 gap-x-6 pt-2 border-t border-gray-100">
            <ValueLabel label="Resource" value={detail.resourceOverview?.resourceId || 'NA'} />
            <ValueLabel label="Operation Type" value={detail.operationTypeDisplay || 'NA'} />
            <ValueLabel label="Created By" value={detail.createdBy || 'NA'} />
            <ValueLabel label="Created On" value={detail.createdDate || 'NA'} />
            <ValueLabel label="Scheduled Date" value={detail.scheduledDate || 'NA'} />
            {isCompleted && (
              <>
                <ValueLabel label="Completed On" value={detail.completedDate || 'NA'} />
                <ValueLabel label="Completed By" value={detail.completedBy || 'NA'} />
              </>
            )}
          </div>
        </div>

        {/* Quick Actions Card (4 cols) */}
        <div className="lg:col-span-4">
          <QuickActions operation={detail} onRefetch={refetchDetail} />
        </div>
      </div>

      {/* Shared Tabs Section */}
      <div className="w-full mt-6">
        <Tabs
          defaultValue="details"
          urlMapping={urlMapping}
          tabComponents={tabComponents}
          tabsListClassName="grid grid-cols-5"
        />
      </div>
    </div>
  );
};
export default Landing;
